"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription, AlertTitle, Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, RadioGroup, RadioGroupItem, Skeleton, Switch, } from "@owlcat/ui";
const DEFAULT_LABELS = {
    heading: "Notifications",
    channelLabel: "How should we reach you?",
    channelEmail: "Email",
    channelTelegram: "Telegram",
    channelOff: "Off",
    addEmail: "Add email",
    activateTelegram: "Activate Telegram",
    emailNotAvailable: "No verified email on file yet.",
    telegramNotAvailable: "Telegram isn't connected yet.",
    notEnabledBadge: "not set up",
    categoriesLabel: "What to notify me about",
    loadError: "Couldn't load notification settings.",
    saveError: "Couldn't save. Please try again.",
    saveBtn: "Save",
    savedNote: "Saved.",
};
/** IANA zones (browser list, small fallback), with current UTC offset + label. */
function ianaZones() {
    try {
        const f = Intl.supportedValuesOf;
        if (f)
            return f("timeZone");
    }
    catch {
        /* fall through */
    }
    return ["UTC", "Europe/Prague", "Europe/Moscow", "Europe/Kyiv", "Europe/London", "Europe/Berlin", "Europe/Paris", "America/New_York", "America/Los_Angeles", "Asia/Dubai"];
}
function zoneOffsetMinutes(tz, now) {
    try {
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset", hour: "2-digit" }).formatToParts(now);
        const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
        const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name);
        if (!m)
            return 0;
        const sign = m[1] === "-" ? -1 : 1;
        return sign * (parseInt(m[2], 10) * 60 + (m[3] ? parseInt(m[3], 10) : 0));
    }
    catch {
        return null;
    }
}
function offsetLabel(mins) {
    const sign = mins < 0 ? "-" : "+";
    const a = Math.abs(mins);
    return `UTC${sign}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}
function buildZoneOptions() {
    const now = new Date();
    return ianaZones()
        .map((id) => {
        const off = zoneOffsetMinutes(id, now) ?? 0;
        return { id, offset: off, label: `${id} (${offsetLabel(off)})` };
    })
        .sort((a, b) => a.offset - b.offset || a.id.localeCompare(b.id));
}
const stripSlash = (u) => u.replace(/\/+$/, "");
/** Is this category on, given the stored map? opt-in: absent = off; else on. */
function categoryOn(cats, c) {
    return c.optIn ? cats[c.key] === true : cats[c.key] !== false;
}
export function NotificationSettings({ accountsUrl, categories, className, labels, timezone, }) {
    const t = { ...DEFAULT_LABELS, ...labels };
    const base = stripSlash(accountsUrl.trim());
    // `state` = last PERSISTED prefs (source of truth for channelsAvailable + the
    // "clean" baseline). `draft` = the user's unsaved edits. Nothing is written
    // until the explicit Save button; toggles/radio only mutate the draft.
    const [state, setState] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [savedOk, setSavedOk] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tgBusy, setTgBusy] = useState(false);
    // Optional timezone sub-control (only when `timezone` prop is passed). Draft
    // model like the prefs: `tz` is the unsaved pick, `tzSaved` the baseline.
    const tzZones = useMemo(() => (timezone ? buildZoneOptions() : []), [timezone]);
    const tzSet = useMemo(() => new Set(tzZones.map((z) => z.id)), [tzZones]);
    const [tzSaved, setTzSaved] = useState(timezone?.initial ?? "");
    const [tz, setTz] = useState(timezone?.initial ?? "");
    const [tzQuery, setTzQuery] = useState(timezone?.initial ?? "");
    const [tzOpen, setTzOpen] = useState(false);
    const [detected, setDetected] = useState("");
    useEffect(() => {
        if (!timezone)
            return;
        try {
            setDetected(Intl.DateTimeFormat().resolvedOptions().timeZone || "");
        }
        catch {
            /* leave '' */
        }
    }, [timezone]);
    const tzMatches = useMemo(() => {
        const q = tzQuery.trim().toLowerCase();
        const list = q ? tzZones.filter((z) => z.id.toLowerCase().includes(q)) : tzZones;
        return list.slice(0, 60);
    }, [tzZones, tzQuery]);
    const tzDirty = !!timezone && tz !== tzSaved && tzSet.has(tz);
    function pickTz(id) {
        setTz(id);
        setTzQuery(id);
        setTzOpen(false);
        setSavedOk(false);
    }
    const availOk = (v, a) => v === "email" ? a.email : v === "telegram" ? a.telegramLinked : true;
    function adopt(prefs) {
        setState(prefs);
        setDraft({ channel: prefs.channel, categories: { ...prefs.categories } });
    }
    // Load on mount (credentialed — the parent-domain owlcat-id cookie rides along).
    useEffect(() => {
        let alive = true;
        setState(null);
        setDraft(null);
        setLoadFailed(false);
        (async () => {
            try {
                const res = await fetch(`${base}/notifications/prefs`, {
                    credentials: "include",
                    headers: { Accept: "application/json" },
                });
                if (!alive)
                    return;
                if (!res.ok) {
                    setLoadFailed(true);
                    return;
                }
                adopt((await res.json()));
            }
            catch {
                if (alive)
                    setLoadFailed(true);
            }
        })();
        return () => {
            alive = false;
        };
    }, [base]);
    // On window focus, re-sync channelsAvailable (the user may have just linked
    // Telegram / added an email in another tab via a connect CTA) so the Save
    // button un-blocks. The user's in-progress DRAFT edits are preserved.
    useEffect(() => {
        async function onFocus() {
            try {
                const res = await fetch(`${base}/notifications/prefs`, {
                    credentials: "include",
                    headers: { Accept: "application/json" },
                });
                if (!res.ok)
                    return;
                const fresh = (await res.json());
                setState((prev) => (prev ? { ...prev, channelsAvailable: fresh.channelsAvailable } : fresh));
            }
            catch {
                /* transient — keep current state */
            }
        }
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [base]);
    // Persist the draft (explicit Save). Blocked in the UI when the chosen channel
    // isn't set up, so a 409 is not expected here; treat any failure as save-error.
    async function saveDraft() {
        if (!state || !draft || saving)
            return;
        setSaving(true);
        setSaveFailed(false);
        setSavedOk(false);
        try {
            const res = await fetch(`${base}/notifications/prefs`, {
                method: "PUT",
                credentials: "include",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ channel: draft.channel, categories: draft.categories }),
            });
            if (!res.ok) {
                setSaveFailed(true);
                return;
            }
            adopt((await res.json()));
            // Persist the timezone on the SAME Save (only if it changed).
            if (tzDirty) {
                const tzRes = await fetch(`${base}/profile/timezone`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ timezone: tz }),
                });
                if (!tzRes.ok) {
                    setSaveFailed(true);
                    return;
                }
                setTzSaved(tz);
            }
            setSavedOk(true);
        }
        catch {
            setSaveFailed(true);
        }
        finally {
            setSaving(false);
        }
    }
    // Radio / toggle handlers mutate the DRAFT only — no network until Save.
    function selectChannel(v) {
        setDraft((d) => (d ? { ...d, channel: v } : d));
        setSaveFailed(false);
        setSavedOk(false);
    }
    function toggleCategory(key, v) {
        setDraft((d) => (d ? { ...d, categories: { ...d.categories, [key]: v } } : d));
        setSaveFailed(false);
        setSavedOk(false);
    }
    // Telegram enable — mirrors the accounts profile: POST /telegram/link/init then
    // open the returned bot deep-link; fall back to the accounts profile page.
    async function activateTelegram() {
        if (tgBusy)
            return;
        setTgBusy(true);
        try {
            const res = await fetch(`${base}/telegram/link/init`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            const data = (await res.json().catch(() => ({})));
            if (res.ok && data.deepLink) {
                window.open(data.deepLink, "_blank", "noreferrer");
            }
            else {
                window.open(`${base}/profile`, "_blank", "noreferrer");
            }
        }
        catch {
            window.open(`${base}/profile`, "_blank", "noreferrer");
        }
        finally {
            setTgBusy(false);
        }
    }
    if (loadFailed) {
        return (_jsxs(Card, { className: className, children: [_jsx(CardHeader, { children: _jsx(CardTitle, { children: t.heading }) }), _jsx(CardContent, { children: _jsx(Alert, { variant: "warning", children: _jsx(AlertDescription, { children: t.loadError }) }) })] }));
    }
    if (!state || !draft) {
        return (_jsxs(Card, { className: className, children: [_jsx(CardHeader, { children: _jsx(CardTitle, { children: t.heading }) }), _jsxs(CardContent, { className: "flex flex-col gap-3", children: [_jsx(Skeleton, { className: "h-9 w-full" }), _jsx(Skeleton, { className: "h-9 w-2/3" })] })] }));
    }
    const avail = state.channelsAvailable;
    const channel = draft.channel;
    const cats = draft.categories;
    const emailMissing = channel === "email" && !avail.email;
    const tgMissing = channel === "telegram" && !avail.telegramLinked;
    const categoriesDisabled = channel === "off";
    const channelReady = availOk(channel, avail);
    // Dirty = the draft differs from the persisted state (channel, any category, or
    // the timezone when that sub-control is enabled).
    const dirty = channel !== state.channel ||
        categories.some((c) => categoryOn(cats, c) !== categoryOn(state.categories, c)) ||
        tzDirty;
    // Save is blocked with nothing to save OR when the chosen channel isn't set up
    // (must connect email/telegram first — the CTA above guides that).
    const saveDisabled = !dirty || !channelReady || saving;
    return (_jsxs(Card, { className: className, children: [_jsx(CardHeader, { children: _jsx(CardTitle, { children: t.heading }) }), _jsxs(CardContent, { className: "flex flex-col gap-5", children: [_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(Label, { children: t.channelLabel }), _jsxs(RadioGroup, { value: channel, onValueChange: (v) => selectChannel(v), className: "gap-2", "aria-label": t.channelLabel, children: [_jsxs("label", { className: "flex items-center gap-2", children: [_jsx(RadioGroupItem, { value: "email" }), _jsx("span", { children: t.channelEmail }), !avail.email && _jsx(Badge, { variant: "secondary", children: t.notEnabledBadge })] }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx(RadioGroupItem, { value: "telegram" }), _jsx("span", { children: t.channelTelegram }), !avail.telegramLinked && _jsx(Badge, { variant: "secondary", children: t.notEnabledBadge })] }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx(RadioGroupItem, { value: "off" }), _jsx("span", { children: t.channelOff })] })] })] }), emailMissing && (_jsxs(Alert, { variant: "warning", children: [_jsx(AlertTitle, { children: t.emailNotAvailable }), _jsx(AlertDescription, { children: _jsx(Button, { asChild: true, variant: "outline", size: "sm", className: "mt-2 w-full sm:w-auto", children: _jsx("a", { href: `${base}/profile`, target: "_blank", rel: "noreferrer", children: t.addEmail }) }) })] })), tgMissing && (_jsxs(Alert, { variant: "warning", children: [_jsx(AlertTitle, { children: t.telegramNotAvailable }), _jsx(AlertDescription, { children: _jsx(Button, { type: "button", variant: "outline", size: "sm", className: "mt-2 w-full sm:w-auto", onClick: activateTelegram, disabled: tgBusy, children: t.activateTelegram }) })] })), categories.length > 0 && (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx(Label, { children: t.categoriesLabel }), categories.map((c) => {
                                const enabled = categoryOn(cats, c);
                                return (_jsxs("label", { className: "flex items-center justify-between gap-3 rounded-xl border bg-card p-3 text-card-foreground", children: [_jsxs("span", { className: "min-w-0 text-sm", children: [c.label, c.description && (_jsx("span", { className: "mt-0.5 block text-xs text-muted-foreground", children: c.description }))] }), _jsx(Switch, { checked: enabled, 
                                            // An opt-in consent stays editable with the channel off:
                                            // saying "stop writing to me" must always be one tap.
                                            disabled: categoriesDisabled && !c.optIn, onCheckedChange: (v) => toggleCategory(c.key, v), "aria-label": c.label })] }, c.key));
                            })] })), timezone && (_jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "notif-tz", children: timezone.label }), _jsxs("div", { className: "relative", children: [_jsx(Input, { id: "notif-tz", value: tzQuery, onChange: (e) => {
                                            setTzQuery(e.target.value);
                                            setTzOpen(true);
                                            setSavedOk(false);
                                        }, onFocus: () => setTzOpen(true), onBlur: () => setTimeout(() => setTzOpen(false), 150), placeholder: timezone.searchPlaceholder, disabled: saving, autoComplete: "off", role: "combobox", "aria-expanded": tzOpen, "aria-controls": "notif-tz-list", className: "w-full" }), tzOpen && tzMatches.length > 0 && (_jsx("ul", { id: "notif-tz-list", role: "listbox", className: "absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md", children: tzMatches.map((z) => (_jsx("li", { role: "option", "aria-selected": z.id === tz, children: _jsx("button", { type: "button", onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    pickTz(z.id);
                                                }, className: "block w-full truncate px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground aria-selected:bg-accent", children: z.label }) }, z.id))) }))] }), _jsxs("div", { className: "min-h-[1.25rem] space-y-0.5", children: [tz && tzSet.has(tz) && (_jsxs("span", { className: "block truncate text-xs text-muted-foreground tabular-nums", children: [tz, " (", offsetLabel(zoneOffsetMinutes(tz, new Date()) ?? 0), ")"] })), detected && detected !== tz && (_jsx("button", { type: "button", className: "block max-w-full truncate text-left text-xs font-medium text-primary hover:underline", onClick: () => pickTz(detected), children: timezone.useDetected.replace("{tz}", detected) }))] })] })), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { type: "button", onClick: saveDraft, disabled: saveDisabled, children: t.saveBtn }), savedOk && !dirty && _jsx("span", { className: "text-sm text-muted-foreground", children: t.savedNote }), saveFailed && _jsx("span", { className: "text-sm text-destructive", children: t.saveError })] })] })] }));
}
